import React from "react";
import { Card, CardMedia, Typography, Box, useTheme } from "@mui/material";
import { getStaticAssetPath } from "../../utils/staticAssets";
import { ROUTES } from "../../constants/routes";

/**
 * Represents a recipe hit from Algolia search.
 */
export interface RecipeHit {
  objectID: string;
  name: string;
  coverImage?: string;
  description?: string;
  ingredients?: string[];
  processes?: string[];
  authorName?: string;
  slug?: string;
  _highlightResult?: {
    [key: string]: {
      value: string;
      matchLevel: string;
      matchedWords: string[];
    };
  };
}

interface RecipeCardProps {
  hit: RecipeHit;
}

/**
 * RecipeCard Component
 *
 * Displays a single recipe as a card with image and title overlay.
 * Used in the recipe directory grid.
 */
const RecipeCard: React.FC<RecipeCardProps> = ({ hit }) => {
  const theme = useTheme();

  const handleClick = () => {
    // Open recipe detail page in new tab
    const recipeId = hit.slug || hit.objectID;
    window.open(`${ROUTES.RECIPE_DETAIL}/${recipeId}`, "_blank");
  };

  // Get the cover image or use a placeholder
  const coverImage = hit.coverImage
    ? getStaticAssetPath(hit.coverImage)
    : "/images/recipe_shape_1.png";

  return (
    <Card
      sx={{
        cursor: "pointer",
        transition: "border-color 0.2s ease-in-out",
        borderRadius: "10px",
        overflow: "hidden",
        position: "relative",
        height: 220,
        border: "2px solid transparent",
        bgcolor: theme.palette.grey[100],
        boxShadow: "none",
        contain: "layout",
        willChange: "border-color",
        "&:hover": {
          borderColor: theme.palette.materiom.seaweed,
          "& .recipe-card-author": {
            opacity: 1,
            maxHeight: "30px",
          },
        },
      }}
      elevation={0}
      onClick={handleClick}
    >
      {/* Cover Image */}
      <CardMedia
        component="img"
        image={coverImage}
        alt={hit.name}
        loading="eager"
        sx={{
          height: "100%",
          width: "100%",
          objectFit: "cover",
        }}
      />

      {/* Title Overlay at Bottom */}
      <Box
        className="recipe-card-overlay"
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          background:
            "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 70%, transparent 100%)",
          px: "12px",
          pt: 4,
          pb: "12px",
        }}
      >
        <Typography
          variant="body1"
          className="recipe-card-title"
          sx={{
            color: "white",
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            lineHeight: 1.3,
          }}
        >
          {hit.name}
        </Typography>
        {hit.authorName && (
          <Typography
            variant="caption"
            className="recipe-card-author"
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              display: "block",
              mt: "-2px",
              opacity: 0,
              maxHeight: 0,
              overflow: "hidden",
              transition: "opacity 0.2s ease-out, max-height 0.2s ease-out",
              fontSize: "14px",
              willChange: "opacity, max-height",
            }}
          >
            by {hit.authorName}
          </Typography>
        )}
      </Box>
    </Card>
  );
};

export default RecipeCard;
